import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Button, renderInput, getIcon, Tag } from "@dishopsaas/design-system";
import {
  addPromotion,
  removePromotion,
} from "../../../components/Order/Order.services";
import { openMessageModal } from "../../../utils/common";
import { TERTIARY } from "../../../constants";
import { PromoCodeSectionProps } from "../../../1_types/paymentSummaryTypes";

const PROMO_TAG_STYLES = {
  maxWidth: "none",
  minWidth: "fit-content",
  minHeight: 36,
  padding: "6px 14px",
  "& .MuiChip-label": {
    whiteSpace: "nowrap",
    fontSize: 14,
    lineHeight: "20px",
    overflow: "visible",
    textOverflow: "clip",
  },
};

const PROMO_CHIP_SX = {
  "& .MuiChip-root": PROMO_TAG_STYLES,
};

const PROMO_INPUT_HIDE_TEXT_SX = {
  "& .MuiOutlinedInput-root": {
    "& .MuiInputBase-input": {
      color: "transparent !important",
      WebkitTextFillColor: "transparent !important",
      caretColor: "transparent",
      pointerEvents: "none",
      paddingRight: 8,
    },
  },
};

interface TypingPromoTagProps {
  promoCode: string;
  onClear: () => void;
}

const TypingPromoTag: React.FC<TypingPromoTagProps> = ({
  promoCode,
  onClear,
}) => (
  <Tag
    label={promoCode}
    color="successLight"
    borderRadius={4}
    deletable
    onDelete={onClear}
    minHeight={36}
    style={PROMO_TAG_STYLES}
  />
);

interface AppliedPromoTagProps {
  promoCode: string;
  isRemoveDisabled: boolean;
  onRemove: () => void;
}

const AppliedPromoTag: React.FC<AppliedPromoTagProps> = ({
  promoCode,
  isRemoveDisabled,
  onRemove,
}) => (
  <Tag
    label={promoCode}
    color="successLight"
    borderRadius={4}
    deletable
    onDelete={isRemoveDisabled ? () => {} : onRemove}
    minHeight={36}
    style={PROMO_TAG_STYLES}
  />
);

const PromoCodeSection: React.FC<PromoCodeSectionProps> = ({
  promotion,
  isPromoApplied,
  promoPath,
  nbUsed,
  totalProductPriceOrder,
}) => {
  const theme = useTheme();
  const [promoCode, setPromoCode] = useState("");
  const [loadingPromotion, setLoadingPromotion] = useState<boolean>(false);
  const [promoMessage, setPromoMessage] = useState("");
  const isRemoveDisabled = nbUsed != null && nbUsed > 0;

  const handleApplyPromoCode = async () => {
    await addPromotion(
      setLoadingPromotion,
      setPromoMessage,
      promoCode,
      totalProductPriceOrder,
    );
  };

  const handleRemovePromo = async () => {
    if (!promoPath) return;

    try {
      setLoadingPromotion(true);
      await removePromotion(promoPath);
      setPromoCode("");
      setPromoMessage("");
    } finally {
      setLoadingPromotion(false);
    }
  };
  const handleConfirmRemovePromo = () => {
    if (isRemoveDisabled || !promoPath) return;

    openMessageModal("Confirmer la suppression du code promo ?", {
      showCancel: true,
      buttonName: "Supprimer",
      buttonCancelName: "Annuler",
      onClick: handleRemovePromo,
    });
  };

  const handlePromoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPromoApplied) return;
    setPromoCode(e.target.value);
  };

  if (isPromoApplied) {
    return renderInput({
      disabled: true,
      spellCheck: false,
      readOnly: true,
      value: "",
      variant: "outlined",
      hasTag: false,
      onChange: () => {},
      leftIcon: (
        <AppliedPromoTag
          promoCode={promotion.promoCode}
          isRemoveDisabled={isRemoveDisabled}
          onRemove={handleConfirmRemovePromo}
        />
      ),
      helperText: (
        <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
          {getIcon("info", {
            size: 14,
            color: theme.palette.success.main,
          })}
          <Typography variant="caption" sx={{ color: "success.main" }}>
            Le code promotionnel a été appliqué.
          </Typography>
        </Box>
      ),
      rightIcon: (
        <Button
          colorType={TERTIARY}
          label="RETIRER"
          onClick={handleConfirmRemovePromo}
          height={30}
          disabled={isRemoveDisabled}
          sx={{ opacity: isRemoveDisabled ? 0.4 : 1 }}
          loading={loadingPromotion}
        />
      ),
    });
  }

  return renderInput({
    disabled: loadingPromotion,
    spellCheck: false,
    value: promoCode,
    variant: "outlined",
    onChange: handlePromoInputChange,
    hasTag: false,
    leftIcon: promoCode ? (
      <TypingPromoTag promoCode={promoCode} onClear={() => setPromoCode("")} />
    ) : undefined,
    sx: promoCode ? PROMO_INPUT_HIDE_TEXT_SX : PROMO_CHIP_SX,
    helperText: promoMessage ? (
      <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
        {getIcon("info", { size: 14 })}
        <Typography variant="caption"> {promoMessage}</Typography>
      </Box>
    ) : null,
    rightIcon: (
      <Button
        colorType={TERTIARY}
        label="Valider"
        onClick={handleApplyPromoCode}
        height={30}
        loading={loadingPromotion}
      />
    ),
  });
};

export default PromoCodeSection;
